/** @jsxImportSource @emotion/react */
import { Container, Grid, Link, Message, Text, useTheme } from "basis";
import { STATIC_RESOURCES } from "constants/contacts";
import { useMemo } from "react";
import { CreditCard } from "../CreditCard/CreditCard";
import { DownloadButton } from "../DownloadButton/DownloadButton";
import { DirectDebitDetails } from "./DirectDebitDetails";
import { DirectDebitRequestDetailsPdf } from "./DirectDebitRequestDetailsPdf";
import { State, ToAccount } from "./types";

const { serviceAgreementUrl } = STATIC_RESOURCES;

interface Props {
  state: State;
}

export const DirectDebitSuccess = ({ state }: Props) => {
  const theme = useTheme();
  const toAccount = state.toAccount as ToAccount;
  const { cardName, cardEndsWith, prfi = false } = toAccount.data;
  const {
    accountName,
    jointAccount,
    bankName,
    bsb,
    accountNumber,
    paymentType,
    paymentAmount,
  } = state.formValues;

  // It's important to memoize the pdf since DownloadButton uses React.memo
  const detailsPdf = useMemo(
    () => (
      <DirectDebitRequestDetailsPdf
        cardName={cardName}
        cardEndsWith={cardEndsWith}
        accountName={accountName}
        jointAccount={jointAccount}
        bankName={bankName}
        bsb={bsb}
        accountNumber={accountNumber}
      />
    ),
    [
      accountName,
      accountNumber,
      bankName,
      bsb,
      cardEndsWith,
      cardName,
      jointAccount,
    ],
  );

  return (
    <Grid rowsGap="6" rowsGap-sm="8">
      <Container bg="grey.t05" padding="4">
        <CreditCard cardName={cardName} cardEndsWith={cardEndsWith} />
      </Container>
      <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
        Direct debit will start from your next statement due date. Please make a
        payment for your current statement if you have not already done so.
      </Message>
      <DirectDebitDetails
        accountName={accountName}
        jointAccount={jointAccount}
        bankName={bankName}
        bsb={bsb}
        accountNumber={accountNumber}
        paymentType={paymentType}
        paymentAmount={paymentAmount}
        prfi={prfi}
      />
      <Container>
        <div
          css={{
            padding: `${theme.space[3]} 0`,
            borderBottom: `${theme.borderWidths[0]} solid ${theme.colors.black}`,
          }}
        >
          <Text>Please download the following documents for your records.</Text>
        </div>
        <div
          css={{
            borderBottom: `${theme.borderWidths[0]} solid ${theme.colors.grey.t10}`,
          }}
        >
          <DownloadButton pdf={detailsPdf}>
            Direct debit request details
          </DownloadButton>
        </div>
        <div
          css={{
            borderBottom: `${theme.borderWidths[0]} solid ${theme.colors.grey.t10}`,
          }}
        >
          <DownloadButton url={serviceAgreementUrl}>
            Direct debit service agreement
          </DownloadButton>
        </div>
      </Container>
      <Container>
        <Link appearance="primary-button" width="180" href="/" newTab={false}>
          Go to overview
        </Link>
      </Container>
    </Grid>
  );
};
